import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Trophy, TrendingUp, Repeat, Wallet, RefreshCw, Crown } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtR = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PRESETS = [
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "Year to date", ytd: true },
  { label: "Last 12 months", days: 365 },
  { label: "All time", all: true },
];

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const startOfYear = () => `${new Date().getFullYear()}-01-01`;

const SORT_TABS = [
  { value: "spend", label: "Highest Spend", icon: Trophy, hint: "Top customers ranked by total Rand spend" },
  { value: "profit", label: "Most Profit", icon: TrendingUp, hint: "Customers who generate the most gross profit (revenue − COGS)" },
  { value: "frequency", label: "Most Frequent", icon: Repeat, hint: "Customers who come back most often (by transaction count)" },
  { value: "account", label: "Largest Accounts", icon: Wallet, hint: "Customers with the biggest outstanding account balance" },
];

export default function TopCustomersTab() {
  const [sortBy, setSortBy] = useState("spend");
  const [startDate, setStartDate] = useState(daysAgo(365));
  const [endDate, setEndDate] = useState(today());
  const [limit, setLimit] = useState(50);
  const [data, setData] = useState({ rows: [], total_revenue_in_window: 0 });
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy, limit };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${API}/customers/top-customers`, { params });
      setData(res.data || { rows: [], total_revenue_in_window: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load top customers");
      setData({ rows: [], total_revenue_in_window: 0 });
    } finally {
      setLoading(false);
    }
  }, [sortBy, startDate, endDate, limit]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applyPreset = (preset) => {
    if (preset.all) {
      setStartDate("");
      setEndDate("");
    } else if (preset.ytd) {
      setStartDate(startOfYear());
      setEndDate(today());
    } else {
      setStartDate(daysAgo(preset.days));
      setEndDate(today());
    }
  };

  const topRow = data.rows[0];
  const sortHint = SORT_TABS.find((t) => t.value === sortBy)?.hint;
  const grandTotal = useMemo(() => data.rows.reduce((s, r) => s + (r.total_spend || 0), 0), [data.rows]);

  return (
    <div className="space-y-4">
      {/* Filters card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" />
            Top Customers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Start date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-9 w-40"
                data-testid="top-customers-start-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-9 w-40"
                data-testid="top-customers-end-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Show top</Label>
              <Input
                type="number"
                min={5}
                max={500}
                value={limit}
                onChange={(e) => setLimit(Math.max(5, Math.min(500, Number(e.target.value) || 50)))}
                className="h-9 w-24"
                data-testid="top-customers-limit"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {PRESETS.map((p) => (
                <Button
                  key={p.label}
                  size="sm"
                  variant="outline"
                  className="h-9 text-xs"
                  onClick={() => applyPreset(p)}
                  data-testid={`top-customers-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchData}
              disabled={loading}
              className="ml-auto h-9"
              data-testid="top-customers-refresh"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Refresh
            </Button>
          </div>

          {/* Sort tabs */}
          <Tabs value={sortBy} onValueChange={setSortBy}>
            <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full">
              {SORT_TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <TabsTrigger key={t.value} value={t.value} className="text-xs" data-testid={`top-customers-sort-${t.value}`}>
                    <Icon className="h-4 w-4 mr-1" />
                    {t.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
          <p className="text-xs text-gray-500 italic">{sortHint}</p>
        </CardContent>
      </Card>

      {/* Hero card — who's #1 */}
      {topRow && (
        <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
          <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-amber-100 border-2 border-amber-300 flex items-center justify-center">
                <Crown className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-amber-700">#1 Customer right now</div>
                <div className="text-xl font-bold text-gray-900" data-testid="top-customer-name">{topRow.customer_name || "Walk-in / Unnamed"}</div>
                <div className="text-xs text-gray-600">
                  {topRow.transaction_count} transactions · avg basket {fmtR(topRow.avg_basket)}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-right">
              <div>
                <div className="text-[10px] uppercase text-gray-500">Spend</div>
                <div className="text-lg font-bold text-emerald-700">{fmtR(topRow.total_spend)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-gray-500">Gross Profit</div>
                <div className="text-lg font-bold text-indigo-700">{fmtR(topRow.gross_profit)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-gray-500">% of top {data.rows.length}</div>
                <div className="text-lg font-bold text-gray-700">
                  {grandTotal > 0 ? ((topRow.total_spend / grandTotal) * 100).toFixed(1) : 0}%
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ranked table */}
      <Card>
        <CardContent className="p-0">
          {loading && data.rows.length === 0 ? (
            <div className="py-10 flex items-center justify-center text-gray-500"><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading...</div>
          ) : data.rows.length === 0 ? (
            <div className="py-10 text-center text-gray-500">No customer activity in the selected window.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-12 text-center">#</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">Gross Profit</TableHead>
                  <TableHead className="text-right">Txns</TableHead>
                  <TableHead className="text-right">Avg basket</TableHead>
                  <TableHead className="text-right">Account bal.</TableHead>
                  <TableHead>Last purchase</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r, idx) => {
                  const lastPurchase = r.last_purchase ? new Date(r.last_purchase).toLocaleDateString("en-ZA") : "—";
                  const rowColor = idx === 0 ? "bg-amber-50/40" : idx < 3 ? "bg-amber-50/20" : "";
                  return (
                    <TableRow key={r.customer_id || idx} className={rowColor} data-testid={`top-customer-row-${idx}`}>
                      <TableCell className="text-center font-bold text-gray-500">{idx + 1}</TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">{r.customer_name || "Unnamed"}</div>
                        <div className="text-[11px] text-gray-500">
                          {r.account_number ? `Acc #${r.account_number}` : "Cash customer"}
                          {r.contact ? ` · ${r.contact}` : ""}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">{r.level}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-emerald-700">{fmtR(r.total_spend)}</TableCell>
                      <TableCell className={`text-right font-mono ${r.gross_profit < 0 ? "text-red-600" : "text-indigo-700"}`}>
                        {fmtR(r.gross_profit)}
                      </TableCell>
                      <TableCell className="text-right">{r.transaction_count}</TableCell>
                      <TableCell className="text-right font-mono text-gray-700">{fmtR(r.avg_basket)}</TableCell>
                      <TableCell className={`text-right font-mono ${r.account_balance < 0 ? "text-red-600" : r.account_balance > 0 ? "text-emerald-700" : "text-gray-500"}`}>
                        {fmtR(r.account_balance)}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">{lastPurchase}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Footer total */}
      {data.rows.length > 0 && (
        <div className="text-right text-sm text-gray-600">
          Top {data.rows.length} customers contributed
          <span className="font-bold text-emerald-700 mx-1">{fmtR(grandTotal)}</span>
          to the store in this window.
        </div>
      )}
    </div>
  );
}
